import { useState, useCallback } from 'react'
import { useSearch } from '@/src/contexts'

import { getFullAddressFromLatLong } from '@/src/utils'

import { CurrentAddressData } from '@/src/interfaces'

const MAPBOX_ACCESS_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN

interface Location {
  latitude: number
  longitude: number
}

interface Coordinates {
  lat: number
  lon: number
}

interface UseCurrentLocationReturn {
  addressData: CurrentAddressData | null
  geoLocationError: string | null
  loadingGeoLocation: boolean
  geoLocation: Location | null
  clearLocation: () => void
  fetchLocation: () => Promise<void>
  getCoordinatesFromAddress: (address: string) => Promise<Coordinates | null>
  isUS: boolean
}

export const useCurrentLocation = (): UseCurrentLocationReturn => {
  const {
    addressData,
    setAddressData,
    geoLocation,
    setGeoLocation,
    clearAllLocationData,
  } = useSearch()
  const [loadingGeoLocation, setLoadingGeoLocation] = useState<boolean>(false)
  const [geoLocationError, setGeoLocationError] = useState<string | null>(null)
  const [isUS, setIsUS] = useState<boolean>(true)

  const clearLocation = useCallback(() => {
    clearAllLocationData()
    setGeoLocationError(null)
  }, [clearAllLocationData])

  const getCoordinatesFromAddress = useCallback(
    async (address: string): Promise<Coordinates | null> => {
      if (!MAPBOX_ACCESS_TOKEN) {
        console.error('Mapbox access token is missing')
        return null
      }

      try {
        // Clean up the address string
        const cleanAddress = address.trim().replace(/\s+/g, ' ')

        // Build the Mapbox API URL with proper parameters
        const url = new URL(
          'https://api.mapbox.com/geocoding/v5/mapbox.places/' +
            encodeURIComponent(cleanAddress) +
            '.json'
        )
        url.searchParams.append('access_token', MAPBOX_ACCESS_TOKEN)
        url.searchParams.append('country', 'US')
        url.searchParams.append('types', 'place,postcode,region,address')
        url.searchParams.append('limit', '1')

        const response = await fetch(url.toString())

        if (!response.ok) {
          throw new Error('Failed to fetch coordinates')
        }

        const data = await response.json()

        if (!data.features || data.features.length === 0) {
          console.error('No results found for address:', cleanAddress)
          return null
        }

        // Get the first (most relevant) result
        const feature = data.features[0]

        // Check if the result is in the US
        const isLocationUS = feature.context?.some(
          (ctx: any) => ctx.id.startsWith('country.') && ctx.short_code === 'us'
        )

        setIsUS(isLocationUS)

        if (!isLocationUS) {
          console.error('Location is not in the US:', cleanAddress)
          return null
        }

        const [lon, lat] = feature.center
        return { lat, lon }
      } catch (error) {
        console.error('Error getting coordinates from address:', error)
        return null
      }
    },
    []
  )

  const fetchLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setGeoLocationError('Geolocation is not supported by your browser.')
      return
    }

    setLoadingGeoLocation(true)
    setGeoLocationError(null)

    return new Promise<void>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (position: GeolocationPosition) => {
          const { latitude, longitude } = position.coords
          setGeoLocation({ latitude, longitude })

          try {
            const currentLocation = await getFullAddressFromLatLong(
              latitude,
              longitude
            )
            setAddressData(currentLocation)
            resolve()
          } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Error'
            setGeoLocationError(
              'Failed to get your location. Please try again.'
            )
            setAddressData(null)
            reject(new Error(errorMessage))
          } finally {
            setLoadingGeoLocation(false)
          }
        },
        (error: GeolocationPositionError) => {
          if (error.code === error.PERMISSION_DENIED) {
            setGeoLocationError(
              'Location access was denied. Please enable location access in your browser settings.'
            )
          } else {
            setGeoLocationError(
              'Failed to get your location. Please try again.'
            )
          }
          setLoadingGeoLocation(false)
          reject(error)
        }
      )
    })
  }, [setGeoLocation, setAddressData])

  return {
    addressData,
    geoLocationError,
    loadingGeoLocation,
    geoLocation,
    clearLocation,
    fetchLocation,
    getCoordinatesFromAddress,
    isUS,
  }
}
